import { useSpring } from "@react-spring/web";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useMeasure from "react-use-measure";

import {
  CONFIRMED_ORDER_VIEW_EXPIRED_TIME,
  CheckoutConfigurationType,
  LocalStorageKeys,
  QRCodeCheckoutStatus,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useStartNewSearch } from "@is/hooks";
import { LocalStorageService } from "@is/services";
import { CheckoutInputFields } from "@is/types";

const useManageConfirmedOrderView = () => {
  const { t } = useTranslation(TranslationContext.ConfirmedOrder);
  const { restartSearch } = useStartNewSearch();
  const [animatedRef, { width }] = useMeasure();

  const { qrCodeCheckout } = useContext(ViewStateContext).content;

  const styles = useSpring({
    width: width,
    config: { duration: CONFIRMED_ORDER_VIEW_EXPIRED_TIME },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      restartSearch();
    }, CONFIRMED_ORDER_VIEW_EXPIRED_TIME);

    return () => clearTimeout(timer);
  }, [restartSearch]);

  const handleHomeButtonClick = useCallback(async () => {
    await restartSearch();
  }, [restartSearch]);

  const getUserConfirmationRequiredTitles = useCallback(
    (checkoutInfo: any): { title: string; subtitle: string } => {
      const checkoutConfigurationType = checkoutInfo
        ? Object.keys(checkoutInfo)[0]
        : "";

      let isPhoneSet = false;
      let isEmailSet = false;
      if (checkoutInfo && checkoutInfo[checkoutConfigurationType]) {
        const fields = checkoutInfo[checkoutConfigurationType];
        const fieldNames = Object.keys(fields);

        if (fieldNames.includes(CheckoutInputFields.PhoneNumber)) {
          isPhoneSet = fields[CheckoutInputFields.PhoneNumber].value.length > 0;
        }

        if (fieldNames.includes(CheckoutInputFields.Email)) {
          isEmailSet = fields[CheckoutInputFields.Email].value.length > 0;
        }

        if (fieldNames.includes(CheckoutInputFields.PhoneNumberOrEmail)) {
          const phoneOrEmail = fields[CheckoutInputFields.PhoneNumberOrEmail];
          if (
            phoneOrEmail.value.length > 0 &&
            phoneOrEmail.value.includes("@")
          ) {
            isEmailSet = true;
          } else if (phoneOrEmail.value.length > 0) {
            isPhoneSet = true;
          }
        }
      }

      if (isPhoneSet && isEmailSet) {
        return {
          title: t("We have sent you email and SMS!"),
          subtitle: t(
            "Please follow the link in the email or SMS to finalize your purchase."
          ),
        };
      } else if (isPhoneSet) {
        return {
          title: t("We have sent you SMS!"),
          subtitle: t(
            "Please follow the link in the SMS to finalize your purchase."
          ),
        };
      } else if (isEmailSet) {
        return {
          title: t("We have sent you email!"),
          subtitle: t(
            "Please follow the link in the email to finalize your purchase."
          ),
        };
      }
      return {
        title: t("Your order is successfully sent!"),
        subtitle: t(
          "Please complete the payment from the link that is sent to your mobile."
        ),
      };
    },
    [t]
  );

  const windowWidth = window.innerWidth;

  const titles = useMemo(() => {
    const checkoutInfo = JSON.parse(
      LocalStorageService.getItem(LocalStorageKeys.CheckoutInfo)
    );

    let checkoutConfigurationType = checkoutInfo
      ? Object.keys(checkoutInfo)[0]
      : "";

    if (qrCodeCheckout.status === QRCodeCheckoutStatus.Success) {
      checkoutConfigurationType = CheckoutConfigurationType.QRCode;
    }

    switch (checkoutConfigurationType) {
      case CheckoutConfigurationType.UserConfirmationRequired:
        return getUserConfirmationRequiredTitles(checkoutInfo);
      case CheckoutConfigurationType.QRCode:
        return {
          title: t("Successfully sent!"),
          subtitle: t("Please check your mobile to complete the payment."),
        };
      case CheckoutConfigurationType.PayAtCounter:
      default:
        return {
          title: t("Your order is successfully confirmed!"),
          subtitle: t("Please complete the payment at the counter."),
        };
    }
  }, [getUserConfirmationRequiredTitles, qrCodeCheckout, t]);

  return {
    t,
    animatedRef,
    styles,
    handleHomeButtonClick,
    windowWidth,
    titles,
  };
};

export default useManageConfirmedOrderView;
